import { useRef, useEffect } from "react";
import { gsap } from "../../lib/gsap";
import Container from "../layout/Container";
import Section from "../layout/Section";

import perceptionImg from "../../assets/images/solution/perception.png";
import radarImg from "../../assets/images/solution/radar.png";
import networkImg from "../../assets/images/solution/network.jpg";

/* ============================= */
/* Animated Block Wrapper */
/* ============================= */
function AnimatedBlock({ children }) {
  const ref = useRef(null);

  useEffect(() => {
    gsap.fromTo(
      ref.current,
      { opacity: 0, y: 80 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ref.current,
          start: "top 85%",
        },
      }
    );
  }, []);

  return <div ref={ref}>{children}</div>;
}

export default function Solution() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        sectionRef.current,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 90%",
          },
        }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section
      id="solution"
      className="relative bg-white text-slate-900 py-32 overflow-hidden"
    >
      {/* Subtle background depth layer */}
      <div className="absolute inset-0 bg-gradient-to-b from-white to-slate-50 pointer-events-none" />

      <Container>
        <div ref={sectionRef} className="relative space-y-32">

          {/* Section Intro */}
          <AnimatedBlock>
            <div className="max-w-4xl">
              <h2 className="text-4xl md:text-6xl font-semibold leading-tight mb-8">
                Unified Autonomy Engine
              </h2>

              <p className="text-slate-600 text-lg leading-relaxed">
                A vertically integrated autonomy stack combining
                perception, predictive intelligence, and deterministic
                control into a scalable deployment-ready platform.
              </p>
            </div>
          </AnimatedBlock>

          {/* ================= PERCEPTION ================= */}
          <AnimatedBlock>
            <div className="grid md:grid-cols-2 gap-20 items-center">

              <div className="group relative rounded-3xl overflow-hidden border border-slate-200 transition-all duration-500 hover:shadow-xl">
                <img
                  src={perceptionImg}
                  alt="Perception system"
                  className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-indigo-500/10 to-transparent transition-opacity duration-500 pointer-events-none" />
              </div>

              <div className="space-y-6">
                <h3 className="text-2xl font-semibold">
                  Real-Time Perception Layer
                </h3>

                <p className="text-slate-600 leading-relaxed">
                  Multi-modal data streams are fused into a structured
                  environmental representation operating at millisecond latency.
                </p>

                <ul className="space-y-3 text-slate-500">
                  <li>• Object detection & semantic classification</li>
                  <li>• Dynamic obstacle tracking</li>
                  <li>• HD lane & map alignment</li>
                </ul>
              </div>
            </div>
          </AnimatedBlock>

          {/* ================= REASONING ================= */}
          <AnimatedBlock>
            <div className="grid md:grid-cols-2 gap-20 items-center">

              <div className="space-y-6">
                <h3 className="text-2xl font-semibold">
                  Predictive Reasoning Engine
                </h3>

                <p className="text-slate-600 leading-relaxed">
                  Behavioral forecasting models anticipate multi-agent
                  interactions and environmental state transitions.
                </p>

                <ul className="space-y-3 text-slate-500">
                  <li>• Multi-agent trajectory forecasting</li>
                  <li>• Risk scoring & uncertainty modeling</li>
                  <li>• Scenario validation pipelines</li>
                </ul>
              </div>

              <div className="group relative rounded-3xl overflow-hidden border border-slate-200 transition-all duration-500 hover:shadow-xl">
                <img
                  src={radarImg}
                  alt="Predictive system"
                  className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-indigo-500/10 to-transparent transition-opacity duration-500 pointer-events-none" />
              </div>
            </div>
          </AnimatedBlock>

          {/* ================= CONTROL ================= */}
          <AnimatedBlock>
            <div className="grid md:grid-cols-2 gap-20 items-center">

              <div className="group relative rounded-3xl overflow-hidden border border-slate-200 transition-all duration-500 hover:shadow-xl">
                <img
                  src={networkImg}
                  alt="Control infrastructure"
                  className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 bg-gradient-to-br from-indigo-500/10 to-transparent transition-opacity duration-500 pointer-events-none" />
              </div>

              <div className="space-y-6">
                <h3 className="text-2xl font-semibold">
                  Deterministic Control Architecture
                </h3>

                <p className="text-slate-600 leading-relaxed">
                  Real-time planning and control layers execute
                  validated decisions across dense urban and
                  high-speed highway scenarios.
                </p>

                <ul className="space-y-3 text-slate-500">
                  <li>• Redundant validation pathways</li>
                  <li>• Real-time trajectory optimization</li>
                  <li>• Fail-safe fallback execution</li>
                </ul>
              </div>
            </div>
          </AnimatedBlock>

        </div>
      </Container>
    </Section>
  );
}